import { apiFetch } from '../lib/apiFetch';

import { GiftPassApiError, type GiftPassDetail } from './gift-passes';

export type GiftPassEmailType =
  | 'GIFT_PASS_PURCHASE_CONFIRMATION'
  | 'GIFT_PASS_RECIPIENT'
  | 'GIFT_PASS_REFUND'
  | 'GIFT_PASS_COMP';

export interface CreateCompGiftPassBody {
  purchaserEmail: string;
  reason: string;
}

export interface ResendGiftPassEmailResult {
  toEmail: string;
  giftPassIds: string[];
}

const BASE = '/api/v1/gift-passes';

const GONE = 'That gift pass no longer exists.';

const COMP_COPY: Record<number, string> = {
  400: 'Some of the details are not valid. Check them and try again.',
  403: "You don't have permission to create comp passes.",
};

const VOID_COPY: Record<number, string> = {
  400: 'Enter a reason of 500 characters or fewer.',
  403: "You don't have permission to void gift passes.",
  404: GONE,
  409: 'This pass can no longer be voided. It may have just been redeemed or voided.',
};

const RESEND_COPY: Record<number, string> = {
  400: 'That email could not be sent again.',
  403: "You don't have permission to re-send gift pass emails.",
  404: GONE,
};

async function failure(
  res: Response,
  copy: Record<number, string>,
  fallback: string,
  useServerMessageFor: number[] = [],
): Promise<GiftPassApiError> {
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  const serverMessage = useServerMessageFor.includes(res.status) ? body.message : undefined;
  return new GiftPassApiError(serverMessage ?? copy[res.status] ?? fallback, res.status);
}

async function post(path: string, body: object): Promise<Response> {
  return apiFetch(`${BASE}${path}`, { method: 'POST', body: JSON.stringify(body) });
}

export async function createCompGiftPass(body: CreateCompGiftPassBody): Promise<GiftPassDetail> {
  const res = await post('', body);
  if (!res.ok) {
    throw await failure(res, COMP_COPY, 'Something went wrong and the comp pass was not created.');
  }
  return ((await res.json()) as { giftPass: GiftPassDetail }).giftPass;
}

export async function voidGiftPass(id: string, reason: string): Promise<GiftPassDetail> {
  const res = await post(`/${encodeURIComponent(id)}/void`, { reason });
  if (!res.ok) {
    throw await failure(res, VOID_COPY, 'Something went wrong and the pass was not voided.');
  }
  return ((await res.json()) as { giftPass: GiftPassDetail }).giftPass;
}

/** A 409 carries the server's own reason, e.g. that the pass was never sent to anyone. */
export async function resendGiftPassEmail(
  id: string,
  type: GiftPassEmailType,
): Promise<ResendGiftPassEmailResult> {
  const res = await post(`/${encodeURIComponent(id)}/resend`, { type });
  if (!res.ok) {
    throw await failure(
      res,
      RESEND_COPY,
      'Something went wrong and the email was not sent.',
      [409],
    );
  }
  return (await res.json()) as ResendGiftPassEmailResult;
}
